import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sms } from '@/api/sms';
import type { SmsMessageRow, SmsDncRow, SmsStatus, SendSmsBody } from '@/api/sms';
import { dids } from '@/api/dids';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useSmsUnreadCount, SMS_UNREAD_QUERY_KEY } from '@/hooks/useSmsUnread';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { formatRef } from '@/lib/formatRef';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/PhoneInput';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  MessageSquare, MessagesSquare, Plus, Send, Ban, Search, ChevronLeft, ChevronRight,
  ShieldOff, Trash2, ArrowLeft, CheckCheck, Radio, ListChecks, Hash, BellRing,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { BroadcastsTab } from './BroadcastsTab';
import { ListsTab } from './ListsTab';
import { KeywordsTab } from './KeywordsTab';
import { SmsNotificationSettingsCard } from './SmsNotificationSettingsCard';

const SEND_ROLES = ['super_admin', 'reseller_owner', 'office_manager'];
const DNC_ADMIN_ROLES = ['super_admin', 'reseller_owner'];

// How often to re-pull messages so inbound replies appear without a manual
// refresh. Used by both the conversations view and the flat log.
const POLL_MS = 15000;

type Tab =
  | 'conversations' | 'log'
  | 'broadcasts' | 'lists' | 'keywords'
  | 'dnc' | 'notifications';

export function SmsPage() {
  const user = useCurrentUser();
  const canSend = SEND_ROLES.includes(user.role);
  const canAdminDnc = DNC_ADMIN_ROLES.includes(user.role);
  // Notification settings expose the account contact email — office_manager+.
  const canSettings = SEND_ROLES.includes(user.role);
  const [tab, setTab] = useState<Tab>('conversations');
  const [sendOpen, setSendOpen] = useState(false);

  return (
    <>
      <PageHeader
        eyebrow="Messaging"
        title="SMS"
        description="Two-way texting, bulk broadcasts, drip campaigns, and keywords. STOP replies auto-add to the DNC list."
        actions={canSend && (
          <Button size="sm" onClick={() => setSendOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            New message
          </Button>
        )}
      />

      <div className="mb-6 flex items-center gap-1 border-b border-border overflow-x-auto">
        <TabButton active={tab === 'conversations'} onClick={() => setTab('conversations')}>
          <MessagesSquare className="h-3.5 w-3.5 mr-1.5" />
          Conversations
        </TabButton>
        <TabButton active={tab === 'log'} onClick={() => setTab('log')}>
          <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
          All messages
        </TabButton>
        <TabButton active={tab === 'broadcasts'} onClick={() => setTab('broadcasts')}>
          <Radio className="h-3.5 w-3.5 mr-1.5" />
          Broadcasts
        </TabButton>
        <TabButton active={tab === 'lists'} onClick={() => setTab('lists')}>
          <ListChecks className="h-3.5 w-3.5 mr-1.5" />
          Lists
        </TabButton>
        <TabButton active={tab === 'keywords'} onClick={() => setTab('keywords')}>
          <Hash className="h-3.5 w-3.5 mr-1.5" />
          Keywords
        </TabButton>
        <TabButton active={tab === 'dnc'} onClick={() => setTab('dnc')}>
          <Ban className="h-3.5 w-3.5 mr-1.5" />
          Do-Not-Contact
        </TabButton>
        {canSettings && (
          <TabButton active={tab === 'notifications'} onClick={() => setTab('notifications')}>
            <BellRing className="h-3.5 w-3.5 mr-1.5" />
            Notifications
          </TabButton>
        )}
      </div>

      {tab === 'conversations' && <ConversationsView canSend={canSend} />}
      {tab === 'log' && <MessageLog />}
      {tab === 'broadcasts' && <BroadcastsTab canMutate={canSend} />}
      {tab === 'lists' && <ListsTab canMutate={canSend} />}
      {tab === 'keywords' && <KeywordsTab canMutate={canSend} />}
      {tab === 'dnc' && <DncPanel canAdmin={canAdminDnc} />}
      {tab === 'notifications' && canSettings && <SmsNotificationSettingsCard />}

      {canSend && <SendSmsDialog open={sendOpen} onOpenChange={setSendOpen} />}
    </>
  );
}

function TabButton({
  active, onClick, children,
}: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors',
        'border-b-2 -mb-px',
        active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

// ── Message log ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 40;

function MessageLog() {
  const qc = useQueryClient();
  const customerId = useEffectiveCustomerId();
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState<'all' | 'inbound' | 'outbound'>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['sms-messages', customerId, offset, filter],
    queryFn: () => sms.listMessages({
      limit: PAGE_SIZE, offset,
      direction: filter === 'all' ? undefined : filter,
      customer_id: customerId ?? undefined,
    }),
    refetchInterval: POLL_MS,
  });
  const rows = data?.rows ?? [];
  const hasMore = rows.length === PAGE_SIZE;

  // Shares the shell hook's cached count — no extra request. Drives the
  // "Mark all read" affordance (global count, not just this page of rows).
  const { count: unreadCount } = useSmsUnreadCount();
  const markAll = useMutation({
    mutationFn: () => sms.markRead({
      all: true,
      ...(customerId ? { customer_id: customerId } : {}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SMS_UNREAD_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ['sms-messages'] });
    },
  });

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <FilterChip active={filter === 'all'} onClick={() => { setFilter('all'); setOffset(0); }}>
          All
        </FilterChip>
        <FilterChip active={filter === 'inbound'} onClick={() => { setFilter('inbound'); setOffset(0); }}>
          Inbound
        </FilterChip>
        <FilterChip active={filter === 'outbound'} onClick={() => { setFilter('outbound'); setOffset(0); }}>
          Outbound
        </FilterChip>
        {unreadCount > 0 && (
          <Button
            size="sm" variant="outline"
            className="ms-auto"
            disabled={markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            <CheckCheck className="h-3.5 w-3.5 me-1.5" />
            {markAll.isPending ? 'Marking…' : `Mark all read (${unreadCount})`}
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Loading messages…</div>
      ) : rows.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-2">
          {rows.map((m) => <MessageBubble key={m.id} message={m} />)}
        </div>
      )}

      <div className="mt-6 flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {rows.length > 0
            ? `Showing ${offset + 1}–${offset + rows.length}`
            : 'No results'}
        </span>
        <div className="flex gap-1">
          <Button
            size="sm" variant="outline"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Prev
          </Button>
          <Button
            size="sm" variant="outline"
            disabled={!hasMore}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function FilterChip({
  active, onClick, children,
}: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors',
        active
          ? 'bg-primary/10 text-primary ring-1 ring-primary/30'
          : 'bg-secondary/40 text-muted-foreground hover:bg-secondary',
      )}
    >
      {children}
    </button>
  );
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="py-16 text-center">
        <MessageSquare className="mx-auto h-8 w-8 text-slate-300 mb-3" />
        <p className="text-sm font-medium text-slate-500">No messages yet</p>
        <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
          Inbound texts land here as soon as a carrier delivers them. Outbound
          sends — including ones blocked by DNC — are recorded too.
        </p>
      </CardContent>
    </Card>
  );
}

// ── Conversations ─────────────────────────────────────────────────────────────

interface Conversation {
  /** The other party's number — the conversation key. */
  party: string;
  /** Our sending/receiving number for this thread (most recent). */
  ourNumber: string;
  messages: SmsMessageRow[];
  last: SmsMessageRow;
  /** Inbound messages not yet read in the portal (read_at IS NULL). */
  unread: number;
}

/**
 * Group a flat message list into per-other-party conversations, entirely
 * client-side.
 *
 * The "other party" is the number that ISN'T ours: for an outbound row that's
 * `to_number`, for an inbound row it's `from_number`. We also track "our"
 * number for the thread (the counterpart on the same row) so a reply sends
 * FROM the right number — taken from the most recent message so it follows
 * the latest number in use.
 *
 * Messages within a thread are sorted oldest→newest (chat order); threads are
 * sorted by most-recent activity, newest first.
 */
function groupConversations(rows: SmsMessageRow[]): Conversation[] {
  const byParty = new Map<string, SmsMessageRow[]>();
  for (const m of rows) {
    const party = m.direction === 'inbound' ? m.from_number : m.to_number;
    if (!party) continue;
    const list = byParty.get(party);
    if (list) list.push(m);
    else byParty.set(party, [m]);
  }

  const convos: Conversation[] = [];
  for (const [party, msgs] of byParty) {
    msgs.sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at));
    const last = msgs[msgs.length - 1];
    if (!last) continue; // unreachable — every group has ≥1 message — keeps TS happy
    const ourNumber = last.direction === 'inbound' ? last.to_number : last.from_number;
    const unread = msgs.reduce(
      (n, m) => n + (m.direction === 'inbound' && !m.read_at ? 1 : 0),
      0,
    );
    convos.push({ party, ourNumber, messages: msgs, last, unread });
  }
  convos.sort((a, b) => +new Date(b.last.created_at) - +new Date(a.last.created_at));
  return convos;
}

function ConversationsView({ canSend }: { canSend: boolean }) {
  const qc = useQueryClient();
  const customerId = useEffectiveCustomerId();
  const [selected, setSelected] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sms-messages', 'conversations', customerId],
    queryFn: () => sms.listMessages({ limit: 500, customer_id: customerId ?? undefined }),
    refetchInterval: POLL_MS,
  });

  const conversations = useMemo(
    () => groupConversations(data?.rows ?? []),
    [data?.rows],
  );

  // Keep selection valid as threads come and go; default to the newest.
  const active = useMemo(() => {
    if (conversations.length === 0) return null;
    return conversations.find((c) => c.party === selected) ?? conversations[0];
  }, [conversations, selected]);

  // ── Read receipts ──────────────────────────────────────────────────────────
  // Viewing a thread marks its inbound messages read. "Visible" means:
  //   - the user explicitly clicked the conversation (mobile + desktop), or
  //   - the md+ two-pane layout, where the default thread shows automatically.
  // On small screens the thread pane is CSS-hidden until a row is tapped, so
  // the auto-selected default must NOT be marked read there.
  const markRead = useMutation({
    mutationFn: (party: string) => sms.markRead({
      party,
      ...(customerId ? { customer_id: customerId } : {}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SMS_UNREAD_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ['sms-messages'] });
    },
  });
  const { mutate: markReadMutate } = markRead;
  // Signature of the last mark call — party + newest message id. Re-marks
  // when ANOTHER message lands in the open thread, but not on the interim
  // renders between mutate() and the refetch that zeroes `unread`.
  const lastMarkedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!active || active.unread === 0) return;
    const explicitlySelected = selected === active.party;
    const twoPane = typeof window !== 'undefined'
      && window.matchMedia('(min-width: 768px)').matches; // Tailwind md
    if (!explicitlySelected && !twoPane) return;
    const sig = `${active.party}:${active.last.id}`;
    if (lastMarkedRef.current === sig) return;
    lastMarkedRef.current = sig;
    markReadMutate(active.party);
  }, [active, selected, markReadMutate]);

  if (isLoading) {
    return <div className="py-12 text-center text-sm text-muted-foreground">Loading conversations…</div>;
  }

  if (conversations.length === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <MessagesSquare className="mx-auto h-8 w-8 text-slate-300 mb-3" />
          <p className="text-sm font-medium text-slate-500">No conversations yet</p>
          <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
            Once you send a text or a caller replies, each phone number you&rsquo;re messaging with
            shows up here as its own thread.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      {/* Desktop: two panes side by side. Mobile: list, then thread when one is picked. */}
      <div className="grid md:grid-cols-[18rem_1fr] md:divide-x divide-border">
        {/* Conversation list */}
        <div className={cn('md:block', active && selected ? 'hidden' : 'block')}>
          <ul className="divide-y divide-border max-h-[34rem] overflow-y-auto">
            {conversations.map((c) => (
              <ConversationListItem
                key={c.party}
                convo={c}
                active={active?.party === c.party}
                onClick={() => setSelected(c.party)}
              />
            ))}
          </ul>
        </div>

        {/* Thread */}
        <div className={cn('md:block', active && selected ? 'block' : 'hidden md:block')}>
          {active ? (
            <Thread
              convo={active}
              canSend={canSend}
              onBack={() => setSelected(null)}
            />
          ) : (
            <div className="flex h-full min-h-[20rem] items-center justify-center p-8 text-center">
              <div>
                <MessagesSquare className="mx-auto h-7 w-7 text-slate-300 mb-2" />
                <p className="text-sm text-muted-foreground">Select a conversation</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function ConversationListItem({
  convo, active, onClick,
}: {
  convo: Conversation; active: boolean; onClick: () => void;
}) {
  const { last, unread } = convo;
  const snippet = last.body?.trim()
    || (last.status === 'blocked_dnc' ? 'Blocked by DNC' : '(no message)');
  const prefix = last.direction === 'outbound' ? 'You: ' : '';

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'w-full text-left px-4 py-3 transition-colors',
          active ? 'bg-primary/5' : 'hover:bg-secondary/40',
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <span className={cn(
            'font-mono text-sm tracking-tight truncate',
            unread > 0 && 'font-semibold',
          )}>
            {convo.party}
          </span>
          <time
            dateTime={last.created_at}
            className="shrink-0 text-[11px] text-muted-foreground tabular-nums"
          >
            {relativeTime(last.created_at)}
          </time>
        </div>
        <div className="mt-0.5 flex items-center gap-2">
          <span className={cn(
            'flex-1 truncate text-xs',
            unread > 0 ? 'text-foreground font-medium' : 'text-muted-foreground',
          )}>
            {prefix}{snippet}
          </span>
          {unread > 0 && (
            <span
              aria-label={`${unread} unread`}
              className="shrink-0 inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold leading-none tabular-nums"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </div>
      </button>
    </li>
  );
}

function Thread({
  convo, canSend, onBack,
}: {
  convo: Conversation; canSend: boolean; onBack: () => void;
}) {
  return (
    <div className="flex h-full flex-col min-h-[20rem] max-h-[34rem]">
      {/* Thread header */}
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <Button
          variant="ghost" size="icon"
          className="md:hidden -ml-2 h-8 w-8"
          onClick={onBack}
          aria-label="Back to conversations"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0">
          <div className="font-mono text-sm tracking-tight truncate">{convo.party}</div>
          <div className="text-[11px] text-muted-foreground truncate">
            via <span className="font-mono">{convo.ourNumber}</span>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
        {convo.messages.map((m) => <MessageBubble key={m.id} message={m} compact />)}
      </div>

      {/* Reply box */}
      {canSend && <ReplyBox party={convo.party} ourNumber={convo.ourNumber} />}
    </div>
  );
}

function ReplyBox({ party, ourNumber }: { party: string; ourNumber: string }) {
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Super-admin / reseller_owner sends REQUIRE customer_id (the send route
  // 400s 'customer_required' otherwise). Forward the entered-customer scope,
  // same as the compose box (CLAUDE.md rule 18).
  const customerId = useEffectiveCustomerId();

  const send = useMutation({
    mutationFn: (payload: SendSmsBody) => sms.send(payload),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['sms-messages'] });
      if (row.status === 'blocked_dnc') {
        setError(`${party} is on the Do-Not-Contact list — the message was logged as blocked, not sent.`);
        return;
      }
      setBody('');
      setError(null);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Send failed.'),
  });

  function submit() {
    const trimmed = body.trim();
    if (!trimmed || send.isPending) return;
    send.mutate({
      from_number: ourNumber,
      to_number: party,
      body: trimmed,
      ...(customerId ? { customer_id: customerId } : {}),
    });
  }

  return (
    <div className="border-t border-border p-3">
      {error && <p role="alert" className="mb-2 px-1 text-xs text-destructive">{error}</p>}
      <div className="flex items-end gap-2">
        <Textarea
          rows={1}
          value={body}
          onChange={(e) => { setBody(e.target.value); if (error) setError(null); }}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter inserts a newline.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={`Reply to ${party}…`}
          className="min-h-[2.5rem] max-h-32 resize-none"
        />
        <Button
          type="button"
          size="icon"
          className="h-10 w-10 shrink-0"
          disabled={!body.trim() || send.isPending}
          onClick={submit}
          aria-label="Send reply"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/** Compact, human-friendly relative timestamp for the conversation list. */
function relativeTime(iso: string): string {
  const then = new Date(iso);
  const diffMs = Date.now() - then.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'now';
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function MessageBubble({ message, compact = false }: { message: SmsMessageRow; compact?: boolean }) {
  const inbound = message.direction === 'inbound';
  const blocked = message.status === 'blocked_dnc';
  // Unread marker shows in the flat log only. In a thread the act of viewing
  // marks the message read almost immediately, so a marker there would just
  // flash and vanish.
  const unread = !compact && inbound && !message.read_at;

  return (
    <div
      className={cn(
        'flex items-start gap-3',
        inbound ? 'justify-start' : 'justify-end',
      )}
    >
      <div className={cn('max-w-[70%] min-w-0', inbound ? 'order-1' : 'order-2')}>
        <div
          className={cn(
            'rounded-2xl px-4 py-2.5 text-sm leading-relaxed break-words',
            'shadow-sm ring-1',
            inbound
              ? cn('bg-white rounded-bl-sm text-slate-900', unread ? 'ring-sky-300' : 'ring-slate-200')
              : blocked
                ? 'bg-rose-50 ring-rose-200 rounded-br-sm text-rose-950'
                : 'bg-primary/95 ring-primary/80 rounded-br-sm text-primary-foreground',
          )}
        >
          {blocked && (
            <div className="mb-1.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-700">
              <ShieldOff className="h-3 w-3" />
              Blocked by DNC — not sent
            </div>
          )}
          {message.body ?? <span className="italic opacity-60">(no body)</span>}
        </div>
        <div
          className={cn(
            'mt-1 flex items-center gap-2 text-[11px] text-muted-foreground',
            inbound ? 'justify-start' : 'justify-end',
          )}
        >
          {unread && (
            <>
              <span className="inline-flex items-center gap-1 font-semibold uppercase text-[10px] tracking-wider text-sky-600">
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-sky-500" />
                Unread
              </span>
              <span className="text-slate-300">•</span>
            </>
          )}
          {/* In a thread the other party is already in the header, so the
              per-bubble number is redundant. Keep it in the flat log. */}
          {!compact && (
            <>
              <span className="font-mono tracking-tight">
                {inbound ? message.from_number : `→ ${message.to_number}`}
              </span>
              <span className="text-slate-300">•</span>
            </>
          )}
          {/* In a thread, status only matters for outbound (delivery state);
              an inbound chip would always read "delivered". The flat log keeps
              showing the chip for every row, unchanged. */}
          {(!compact || !inbound) && (
            <>
              <StatusChip status={message.status} reason={message.failure_reason} />
              <span className="text-slate-300">•</span>
            </>
          )}
          <time dateTime={message.created_at}>
            {new Date(message.created_at).toLocaleString(undefined, {
              month: 'short', day: 'numeric',
              hour: 'numeric', minute: '2-digit',
            })}
          </time>
        </div>
        {/* Delivery-failure reason (migration 0117): shown as subtext under
            failed rows so the operator sees WHY (e.g. 10DLC registration,
            carrier filtering) without digging into carrier portals. */}
        {message.status === 'failed' && message.failure_reason && (
          <p
            className={cn(
              'mt-0.5 text-[11px] text-rose-600/90 break-words',
              inbound ? 'text-left' : 'text-right',
            )}
          >
            {message.failure_reason}
          </p>
        )}
      </div>
    </div>
  );
}

const STATUS_STYLE: Record<SmsStatus, string> = {
  queued:      'text-amber-700',
  sent:        'text-sky-700',
  delivered:   'text-emerald-700',
  failed:      'text-rose-700',
  blocked_dnc: 'text-rose-700',
};

function StatusChip({ status, reason }: { status: SmsStatus; reason?: string | null }) {
  return (
    <span
      className={cn(
        'font-semibold uppercase text-[10px] tracking-wider',
        STATUS_STYLE[status],
        status === 'failed' && reason && 'cursor-help underline decoration-dotted underline-offset-2',
      )}
      title={status === 'failed' && reason ? reason : undefined}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

// ── DNC panel ───────────────────────────────────────────────────────────────

function DncPanel({ canAdmin }: { canAdmin: boolean }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['sms-dnc', search],
    queryFn: () => sms.listDnc({ search: search.trim() || undefined }),
  });
  const rows = data?.rows ?? [];

  const remove = useMutation({
    mutationFn: (e164: string) => sms.removeDnc(e164),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-dnc'] }),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-sm">Do-Not-Contact list</CardTitle>
          <CardDescription>
            Reseller-wide. Once a number is here, no customer under your reseller
            can text it. STOP replies add automatically.
          </CardDescription>
        </div>
        {canAdmin && (
          <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add number
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="mb-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search E.164 number…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center">
            <Ban className="mx-auto h-6 w-6 text-slate-300 mb-2" />
            <p className="text-sm text-muted-foreground">
              {search ? 'No matches.' : 'No numbers on your DNC list yet.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border -my-2">
            {rows.map((r) => (
              <DncRow
                key={r.id} row={r}
                canAdmin={canAdmin}
                onRemove={() => remove.mutate(r.e164)}
                removing={remove.isPending}
              />
            ))}
          </ul>
        )}
      </CardContent>
      {canAdmin && <AddDncDialog open={addOpen} onOpenChange={setAddOpen} />}
    </Card>
  );
}

function DncRow({
  row, canAdmin, onRemove, removing,
}: {
  row: SmsDncRow; canAdmin: boolean; onRemove: () => void; removing: boolean;
}) {
  return (
    <li className="flex items-center justify-between py-3">
      <div className="min-w-0">
        <div className="font-mono text-sm tracking-tight">{row.e164}</div>
        <div className="mt-0.5 text-xs text-muted-foreground flex items-center gap-2">
          {row.reason && <span className="italic">{row.reason}</span>}
          {row.reason && <span className="text-slate-300">•</span>}
          <time dateTime={row.created_at}>
            added {new Date(row.created_at).toLocaleDateString()}
          </time>
        </div>
      </div>
      {canAdmin && (
        <Button
          size="sm" variant="ghost"
          onClick={onRemove} disabled={removing}
          aria-label={`Remove ${row.e164} from DNC`}
        >
          <Trash2 className="h-3.5 w-3.5 text-slate-400" />
        </Button>
      )}
    </li>
  );
}

function AddDncDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const [e164, setE164] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () => sms.addDnc(e164.trim(), reason.trim() || null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sms-dnc'] });
      setE164(''); setReason(''); setError(null);
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Add failed.'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add to DNC</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="dnc-e164">Phone number</Label>
            <PhoneInput
              id="dnc-e164"
              value={e164}
              onChange={setE164}
              inputClassName="font-mono"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="dnc-reason">Reason (optional)</Label>
            <Input
              id="dnc-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. customer request"
            />
            <p className="text-xs text-muted-foreground">
              Shown in the DNC list — don&rsquo;t put personal details here.
            </p>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={add.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            type="button"
            disabled={add.isPending || !e164.trim()}
            onClick={() => add.mutate()}
          >
            {add.isPending ? 'Adding…' : 'Add to DNC'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Send SMS dialog ─────────────────────────────────────────────────────────

function SendSmsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  // Send routes for super_admin / reseller_owner REQUIRE customer_id in the
  // body (400 customer_required otherwise) — always forward the effective
  // scope when we have one, same as ReplyBox (CLAUDE.md "forwarding
  // customer_id from dialogs").
  const customerId = useEffectiveCustomerId();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [wasBlocked, setWasBlocked] = useState(false);

  // ── From-number options: the customer's own active numbers ────────────────
  // Fetched lazily when the dialog opens. Falls back to a free-text input
  // when there's no customer scope to list numbers for (super_admin /
  // reseller_owner outside a customer context), when the customer has no
  // numbers, or when the lookup fails — so sending stays possible.
  const didsQuery = useQuery({
    queryKey: ['dids', 'sms-from-options', customerId],
    queryFn: () => dids.list({ customerId: customerId!, limit: 200 }),
    enabled: open && Boolean(customerId),
    staleTime: 60_000,
  });
  const fromOptions = useMemo(
    () => (didsQuery.data?.rows ?? []).filter((d) => d.active),
    [didsQuery.data?.rows],
  );
  const useFreeText = !customerId
    || didsQuery.isError
    || (didsQuery.isSuccess && fromOptions.length === 0);

  useEffect(() => {
    if (!open || useFreeText || !didsQuery.isSuccess) return;
    // Exactly one number → preselect it.
    if (fromOptions.length === 1 && !from) {
      setFrom(fromOptions[0]!.e164);
      return;
    }
    // Stale value (e.g. customer context changed while the page was open) —
    // clear it so the trigger shows the placeholder instead of a number the
    // customer doesn't own.
    if (from && !fromOptions.some((d) => d.e164 === from)) setFrom('');
  }, [open, useFreeText, didsQuery.isSuccess, fromOptions, from]);

  const send = useMutation({
    mutationFn: (payload: SendSmsBody) => sms.send(payload),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['sms-messages'] });
      if (row.status === 'blocked_dnc') {
        setWasBlocked(true);
        return;
      }
      setFrom(''); setTo(''); setBody(''); setError(null); setWasBlocked(false);
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Send failed.'),
  });

  const chars = body.length;
  const over = chars > 160;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setWasBlocked(false); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send a message</DialogTitle>
        </DialogHeader>

        {wasBlocked ? (
          <div className="py-4 text-center space-y-3">
            <ShieldOff className="mx-auto h-8 w-8 text-rose-500" />
            <div>
              <p className="text-sm font-semibold">Blocked by DNC</p>
              <p className="text-xs text-muted-foreground max-w-sm mx-auto mt-1">
                <span className="font-mono">{to}</span> is on your reseller&rsquo;s
                Do-Not-Contact list. The message was logged as blocked and not
                sent to the carrier.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => { setWasBlocked(false); onOpenChange(false); }}>
              Close
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="sms-from">From</Label>
                  {useFreeText ? (
                    <Input
                      id="sms-from" className="font-mono"
                      value={from} onChange={(e) => setFrom(e.target.value)}
                      placeholder="+15551112222"
                    />
                  ) : (
                    <Select
                      value={from || undefined}
                      onValueChange={setFrom}
                      disabled={didsQuery.isLoading}
                    >
                      <SelectTrigger id="sms-from" className="font-mono">
                        <SelectValue
                          placeholder={didsQuery.isLoading ? 'Loading numbers…' : 'Choose a number'}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {fromOptions.map((d) => (
                          <SelectItem key={d.id} value={d.e164} className="font-mono">
                            {d.e164}
                            {d.ref_number != null && (
                              <span className="ms-2 text-xs text-muted-foreground">
                                {formatRef(d.ref_number, 'DID')}
                              </span>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {useFreeText
                      ? 'Enter a sending number in E.164.'
                      : 'One of this account’s numbers.'}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sms-to">To</Label>
                  <PhoneInput
                    id="sms-to"
                    inputClassName="font-mono"
                    value={to}
                    onChange={setTo}
                    customerId={customerId ?? undefined}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="sms-body">Message</Label>
                <Textarea
                  id="sms-body" rows={5}
                  value={body} onChange={(e) => setBody(e.target.value)}
                  placeholder="Type your message…"
                />
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">
                    Ivrello checks DNC before handing off to the carrier.
                  </span>
                  <span className={cn(
                    'font-mono tabular-nums',
                    over ? 'text-amber-600' : 'text-muted-foreground',
                  )}>
                    {chars}/160{over ? ' • multi-part' : ''}
                  </span>
                </div>
              </div>
              {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={send.isPending}>Cancel</Button>
              </DialogClose>
              <Button
                type="button"
                disabled={send.isPending || !from.trim() || !to.trim() || !body.trim()}
                onClick={() => send.mutate({
                  from_number: from.trim(),
                  to_number: to.trim(),
                  body: body.trim(),
                  ...(customerId ? { customer_id: customerId } : {}),
                })}
              >
                <Send className="h-3.5 w-3.5 mr-1.5" />
                {send.isPending ? 'Sending…' : 'Send'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
